import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import IValidatedResult = app.interfaces.IValidatedResult;
import ImplementationOwners = Boo.Objects.Enums.ImplementationOwners;
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { FormControl, FormGroup } from '@angular/forms';
import { catchError, finalize, forkJoin, map, Observable, of, throwError } from 'rxjs';
import CustomValidators from '../../../shared/custom-validators';
import { CustomerKeyValuePairService } from '../../../services/customer-keyvaluepair.service';
import KeyEnum = Boo.Objects.Enums.KeyEnum;
import CustomerHelper from 'app/managecustomer/CustomerHelper';

@Component({
  selector: 'app-components-publish-options',
  templateUrl: './publish-options.component.html'
})
export class PublishOptionsComponent implements OnInit {
  @Input() customerId: number;
  @Input() partnerImplementationContactEmail: string;
  @Input() userId: number;
  @Output() implementationOwnerChanged: EventEmitter<ImplementationOwners> = new EventEmitter<ImplementationOwners>();

  formGroup: FormGroup;
  implementationOwnerControl: FormControl<number>;
  customEmailControl: FormControl<string>;

  isLoading = true;
  customerKeyValuePairs: Boo.Objects.CustomerKeyValuePair[] = [];
  implementationOwner: ImplementationOwners = ImplementationOwners.NotSelected;
  customImplementationEmail: string;
  // store the loaded values, so save() can tell whether the user changed anything
  private initialImplementationOwner: ImplementationOwners = ImplementationOwners.NotSelected;
  private initialCustomImplementationEmail = '';
  implementationOwners_Partner = ImplementationOwners.Partner;
  implementationOwners_Custom = ImplementationOwners.Custom;

  implementationOwnerOptions = [
    {
      value: ImplementationOwners.Boostability,
      label: 'Send to Boostability Implementation Team',
      helpKey: 'PublishOption_Boostability'
    },
    {
      value: ImplementationOwners.Customer,
      label: 'Send to Customer Contacts',
      helpKey: 'PublishOption_Customer'
    },
    {
      value: ImplementationOwners.Partner,
      label: 'Send to Partner Implementation Team',
      helpKey: 'PublishOption_Partner'
    },
    {
      value: ImplementationOwners.Custom,
      label: 'Send to Custom Email',
      helpKey: 'PublishOption_CustomEmail'
    }
  ];

  constructor(private customerKeyValuePairService: CustomerKeyValuePairService) { }

  ngOnInit(): void {
    this.customerKeyValuePairService.get(this.customerId)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (customerKeyValuePairs) => {
          this.customerKeyValuePairs = customerKeyValuePairs;
          this.implementationOwner = CustomerHelper.getNumber(customerKeyValuePairs, KeyEnum.ImplementationOwner) || ImplementationOwners.NotSelected;
          this.customImplementationEmail = CustomerHelper.getValue(customerKeyValuePairs, KeyEnum.CustomImplementationEmail) || '';
          this.initialImplementationOwner = this.implementationOwner;
          this.initialCustomImplementationEmail = this.customImplementationEmail;
          this.setupForm();
        },
        error: (err) => toastr.error('Failed to load publish options:', err)
      });
  }

  save(): Observable<void> {
    const selectedImplementationOwner = this.implementationOwnerControl.value;
    const customEmail = selectedImplementationOwner === ImplementationOwners.Custom ? this.customEmailControl.value : '';

    // save() runs on each parent save (e.g., saving website credentials on Logins & Google Setup),
    // not just when publish options change. only persist when the user actually changes something,
    // otherwise it can unintenionally overwrite publish options.
    if (selectedImplementationOwner === this.initialImplementationOwner && customEmail === this.initialCustomImplementationEmail) {
      return of(void 0);
    }

    this.implementationOwner = selectedImplementationOwner;

    if (selectedImplementationOwner !== ImplementationOwners.Custom) {
      this.customEmailControl.setValue('');
    }

    this.customImplementationEmail = customEmail;
    CustomerHelper.setNumber(this.customerKeyValuePairs, this.customerId, this.userId, KeyEnum.ImplementationOwner, selectedImplementationOwner);
    CustomerHelper.setValue(this.customerKeyValuePairs, this.customerId, this.userId, KeyEnum.CustomImplementationEmail, customEmail);

    return forkJoin([
      this.customerKeyValuePairService.save(CustomerHelper.getCustomerKeyValuePair(this.customerKeyValuePairs, KeyEnum.ImplementationOwner)),
      this.customerKeyValuePairService.save(CustomerHelper.getCustomerKeyValuePair(this.customerKeyValuePairs, KeyEnum.CustomImplementationEmail))
    ]).pipe(
      map(() => {
        this.initialImplementationOwner = selectedImplementationOwner;
        this.initialCustomImplementationEmail = customEmail;
      }),
      catchError(err => {
        toastr.error('Failed to save publish options: ', err);
        return throwError(() => err);
      })
    );
  }

  validate(saveType: SaveTypes): IValidatedResult {
    const validatedResult: IValidatedResult = {
      isValid: true,
      errorMessages: []
    } as IValidatedResult;

    if (saveType === SaveTypes.Complete && !this.isValid()) {
      validatedResult.errorMessages.push('Please select a publish option and correct any errors.');
      validatedResult.isValid = false;
    }

    return validatedResult;
  }

  private isValid(): boolean {
    this.implementationOwnerControl.markAsDirty();
    this.implementationOwnerControl.updateValueAndValidity();

    this.customEmailControl.markAsDirty();
    this.customEmailControl.updateValueAndValidity();

    return this.formGroup.valid;
  }

  private setupForm(): void {
    this.implementationOwnerControl = new FormControl(this.implementationOwner, [CustomValidators.valuesNotAllowed([ImplementationOwners.NotSelected], 'Please select a publish option')]);
    this.customEmailControl = new FormControl(this.customImplementationEmail, [CustomValidators.requiredIfValidator(() => this.implementationOwnerControl.value === ImplementationOwners.Custom), CustomValidators.email]);

    this.formGroup = new FormGroup({
      implementationOwner: this.implementationOwnerControl,
      customEmail: this.customEmailControl
    });

    this.implementationOwnerControl.valueChanges.subscribe((value: ImplementationOwners) => {
      this.implementationOwnerChanged.emit(value);
    });

    this.implementationOwnerChanged.emit(this.implementationOwner);
  }
}